import { Gear } from '@phosphor-icons/react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clearCachedToken, post, renewToken } from 'src/api/requests';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { RadioTab } from 'src/components/RadioTab';
import { Tooltip } from 'src/components/Tooltip';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useOrganizationSwitcher } from 'src/hooks/useOrganizationSwitcher';
import { Organization } from 'src/interfaces/organization.interface';
import { EnvironmentsTab } from './components/EnvironmentsTab';
import { UsersTab } from './components/UsersTab';

export const Settings = () => {
  const { patch } = useAuthedRequest();
  const notification = useNotification();
  const [searchParams] = useSearchParams();

  const {
    organizations,
    currentOrg,
    orgsLoading,
    getOrganizationOptions,
    handleChangeOrganization,
    refetchOrganizations,
  } = useOrganizationSwitcher({ returnTo: '/settings' });

  const [loading, setLoading] = useState(false);

  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [modalOrganizationOpen, setModalOrganizationOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'users' ? 'users' : 'environments',
  );

  const isLoading = loading || orgsLoading;

  const handleEditOrg = (orgId: string) => {
    const org = organizations.find((org) => org.id === orgId);
    if (!org) return;

    setEditingOrg(org);
    setNewName(org.name);
    setModalOrganizationOpen(true);
  };

  const updateOrganization = async () => {
    if (!editingOrg) return;

    setLoading(true);
    try {
      await patch(`/organizations/${editingOrg.id}`, {
        name: newName,
      });

      if (currentOrg?.value === editingOrg.id) {
        clearCachedToken();

        await renewToken();

        await post(`/users/sync`, {});
      }

      setModalOrganizationOpen(false);

      notification.success('Organization updated successfully');

      await refetchOrganizations();

      clearCachedToken();

      await renewToken();
    } catch {
      notification.error('Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1">
      <Heading
        title={
          <>
            <h1>
              Settings
              {currentOrg && <> for</>}
            </h1>

            <AutoCompleteSelectBox
              key={String(currentOrg?.value) + '-' + String(currentOrg?.label)}
              defaultValue={currentOrg}
              fetchOptions={getOrganizationOptions}
              onChange={(value) => handleChangeOrganization(value)}
              className="text-xl font-bold"
              variant="text"
              size="medium"
              disabled={isLoading}
              defaultOptions={organizations.map((org) => ({
                label: org.name,
                value: org.id,
              }))}
              allowRemoveOption={false}
            />

            <Tooltip content="Change Organization details" position="bottom">
              <Button
                variant="text"
                size="small"
                onClick={() => handleEditOrg(currentOrg?.value || '')}
              >
                <Gear className="w-6 h-6 text-primary" />
              </Button>
            </Tooltip>
          </>
        }
        subtitle={
          <div className="flex flex-row items-center gap-x-2">
            {currentOrg && (
              <>
                <strong>ID:</strong>{' '}
                <CopyableField
                  value={currentOrg.value}
                  notification={notification}
                />
              </>
            )}
          </div>
        }
        className="border-b-0"
      ></Heading>

      {isLoading && <Loading className="p-4" />}

      {!isLoading && (
        <>
          <RadioTab
            onChange={(value) => {
              setActiveTab(value as string);
            }}
            items={[
              { label: 'Environments', value: 'environments' },
              { label: 'Users', value: 'users' },
            ]}
            className="pl-2"
            value={activeTab}
          />

          <ContentWrapper>
            {activeTab === 'environments' && <EnvironmentsTab />}

            {activeTab === 'users' && <UsersTab />}
          </ContentWrapper>
        </>
      )}

      <Modal
        title="Edit Organization"
        isOpen={modalOrganizationOpen}
        onClose={() => setModalOrganizationOpen(false)}
        actionButton={
          <Button
            onClick={updateOrganization}
            disabled={loading || !newName || newName === editingOrg?.name}
          >
            Update Organization
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-6">
          <div>
            <Input
              label="Organization Name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
